import { useEffect, useRef, useState } from 'react';

interface UseFadeInOptions {
  delay?: number;
  threshold?: number;
  skipInitialCheck?: boolean;
}

export const useFadeIn = <T extends HTMLElement = HTMLDivElement>(options: UseFadeInOptions = {}) => {
  const { delay = 0, threshold = 0.1, skipInitialCheck = false } = options;
  const elementRef = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(() => {
    // Initial state: assume not visible to prevent flash
    return false;
  });

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    let observer: IntersectionObserver | null = null;

    // Check if element is already in viewport on mount (prevents glitch)
    const checkVisibility = () => {
      if (!skipInitialCheck) {
        const rect = element.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInViewport) {
          // Trigger animation immediately for elements already visible
          setIsVisible(true);
          return true; // Skip observer setup since already visible
        }
      }
      return false;
    };

    // Use requestAnimationFrame to check on next frame (prevents flicker)
    requestAnimationFrame(() => {
      // Check if already visible
      if (checkVisibility()) {
        return;
      }

      // If not visible, set up observer
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              if (observer && element) {
                observer.unobserve(element);
              }
            }
          });
        },
        { threshold }
      );

      observer.observe(element);
    });

    return () => {
      if (observer && element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, skipInitialCheck]);

  return { ref: elementRef, isVisible, delay };
};

